import React from 'react';
import { 
  CheckCircle, 
  Search, 
  Award, 
  BookOpen, 
  Briefcase, 
  ShieldCheck, 
  ChevronRight 
} from 'lucide-react';

const Verifiedcertificates = () => {
  return (
    <div style={{marginTop:"60px"}} className="min-h-screen bg-[#f8fafc] pb-20 font-sans">
      {/* Header Section (from image_b89d76.png) */}
      <header className="pt-16 pb-12 text-center px-4">
        <div className="inline-flex items-center gap-2 bg-[#f0f9ff] text-[#0ea5e9] px-4 py-1.5 rounded-full border border-[#e0f2fe] mb-8">
          <CheckCircle size={14} />
          <span className="text-[10px] font-bold tracking-widest uppercase">Official Certification Portal</span>
        </div>
        
        <h1 className="text-6xl font-serif text-[#1e293b] mb-6">
          CodeAlpha Skill Certification <span className="text-[#0ea5e9]">Portal</span>
        </h1>
        
        <div className="max-w-2xl mx-auto space-y-1">
          <p className="text-slate-500 text-lg">
            Validate the authenticity of professional skill certifications issued by CodeAlpha.
          </p>
          <p className="text-slate-500 text-lg">
            Search for unique certificate IDs to verify course completions.
          </p>
        </div>
      </header>

      {/* Main Content Grid (from image_b89a8d.png) */}
      <main className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Left Column: Quick Actions & Trust Card */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm">
            <h3 className="text-xl font-serif text-[#1e293b] mb-6">Quick Actions</h3>
            
            <div className="space-y-3">
              {/* Action 1 */}
              <div className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50/30 transition-all cursor-pointer group">
                <div className="flex items-center gap-4">
                  <div className="p-2.5 bg-slate-50 rounded-xl text-blue-500">
                    <Award size={20} />
                  </div>
                  <div>
                    <p className="font-bold text-[#1e293b] text-sm">View My Certificates</p>
                    <p className="text-[10px] text-slate-400">Access your earned credentials</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-blue-500" />
              </div>

              {/* Action 2 */}
              <div className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50/30 transition-all cursor-pointer group">
                <div className="flex items-center gap-4">
                  <div className="p-2.5 bg-slate-50 rounded-xl text-emerald-500">
                    <BookOpen size={20} />
                  </div>
                  <div>
                    <p className="font-bold text-[#1e293b] text-sm">Earn New Certificate</p>
                    <p className="text-[10px] text-slate-400">Take skill assessments now</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-emerald-500" />
              </div>

              {/* Action 3 */}
              <div className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50/30 transition-all cursor-pointer group">
                <div className="flex items-center gap-4">
                  <div className="p-2.5 bg-slate-50 rounded-xl text-orange-500">
                    <Briefcase size={20} />
                  </div>
                  <div>
                    <p className="font-bold text-[#1e293b] text-sm">Verify Internship Cert</p>
                    <p className="text-[10px] text-slate-400">Verify internship experience</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-orange-500" />
              </div>
            </div>
          </div>

          {/* Verified Trust Card */}
          <div className="bg-[#0086c9] rounded-[2.5rem] p-8 text-white relative overflow-hidden group">
            <div className="relative z-10">
              <ShieldCheck size={32} className="mb-6 opacity-80" />
              <h4 className="text-2xl font-serif mb-4">Verified Trust</h4>
              <p className="text-blue-50/80 text-sm leading-relaxed">
                Every CodeAlpha certificate comes with a unique tamper-proof ID that can be globally verified by employers and institutions.
              </p>
            </div>
            {/* Subtle background decoration */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-2xl transition-transform group-hover:scale-150"></div>
          </div>
        </div>

        {/* Right Column: Verification Form */}
        <div className="lg:col-span-8">
          <div className="bg-white rounded-[2.5rem] p-12 border border-slate-100 shadow-sm min-h-[450px] flex flex-col items-center justify-center">
            <div className="w-full max-w-lg">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-[#f0f9ff] text-[#0ea5e9] rounded-2xl flex items-center justify-center border border-[#e0f2fe]">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <h2 className="text-3xl font-serif text-[#1e293b]">Verify Skill Credential</h2>
                  <p className="text-slate-400 text-sm">Enter your Skill Certificate ID to validate</p>
                </div>
              </div>

              <div className="space-y-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-slate-400 tracking-widest uppercase ml-1">
                    Certificate ID
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none">
                      <Search size={22} className="text-slate-300" />
                    </div>
                    <input 
                      type="text" 
                      placeholder="e.g. CA-SKILL-123456"
                      className="w-full pl-16 pr-8 py-6 bg-white border-2 border-slate-100 rounded-3xl text-xl focus:outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/5 transition-all placeholder:text-slate-200"
                    />
                  </div>
                </div>

                <button className="w-full bg-[#7dc3e7] hover:bg-[#0ea5e9] text-white font-bold py-6 rounded-2xl flex items-center justify-center gap-3 transition-all text-xl shadow-lg shadow-blue-500/10">
                  <Search size={24} />
                  Verify Knowledge
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Verifiedcertificates;